import React from 'react';

import { useIntl } from 'react-intl';

import { GET_ALLOW_LIST_RESOURCE } from 'src/features/allowList/api/useGetAllowList';
import { LIST_ALLOW_LISTS_RESOURCE } from 'src/features/allowList/api/useListAllowLists';
import { useUpdateAllowList } from 'src/features/allowList/api/useUpdateAllowList';
import AllowListFormModal from 'src/features/allowList/components/forms/AllowListFormModal';
import { allowListMessages } from 'src/features/allowList/utils/allowListMessages';
import { ALLOW_LIST_TYPE, AllowList } from 'src/features/allowList/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface UpdateAllowListFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAllowList: AllowList;
    children?: React.ReactNode;
}

export default function UpdateAllowListFormModal<TData>({
    className,
    semantics,
    children,
    selectedAllowList,
}: UpdateAllowListFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const allowListType = selectedAllowList.type;

    const successTitle =
        allowListType === ALLOW_LIST_TYPE.WHITE
            ? allowListMessages.updateWhiteListSuccess
            : allowListMessages.updateBlackListSuccess;
    const successDescription =
        allowListType === ALLOW_LIST_TYPE.WHITE
            ? allowListMessages.updateWhiteListSuccessDescription
            : allowListMessages.updateBlackListSuccessDescription;
    const modalSubtitle =
        allowListType === ALLOW_LIST_TYPE.WHITE
            ? allowListMessages.editWhiteListSubtitle
            : allowListMessages.editBlackListSubtitle;

    const { mutate, isPending } = useUpdateAllowList({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ALLOW_LISTS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [GET_ALLOW_LIST_RESOURCE] });
            toast({
                title: formatMessage(successTitle),
                description: formatMessage(successDescription, {
                    entry: success.name,
                }),
            });
        },
    });

    return (
        <AllowListFormModal
            selectedAllowList={selectedAllowList}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ data, listId: selectedAllowList.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(modalSubtitle, {
                entry: selectedAllowList.name,
            })}
        >
            {children}
        </AllowListFormModal>
    );
}
